import React, { useEffect, useRef, useState } from "react";
import "./GenerativeMusic.css";
import * as Tone from "tone";

// --- SAFETY HELPERS ---
function isValidNote(note) {
  // Accepts only non-empty strings, not "undefined", not null, not Infinity
  return (
    typeof note === "string" &&
    note.trim() !== "" &&
    note !== "undefined" &&
    note !== "null" &&
    note !== "NaN" &&
    note !== "Infinity"
  );
}
function isValidFrequency(freq) {
  return typeof freq === "number" && isFinite(freq);
}
function safeTriggerAttackRelease(synth, noteOrFreq, dur, time) {
  if (isValidNote(noteOrFreq)) {
    synth.triggerAttackRelease(noteOrFreq, dur, time);
  } else if (isValidFrequency(noteOrFreq)) {
    synth.triggerAttackRelease(noteOrFreq, dur, time);
  }
}
function safeTriggerAttackReleaseChord(synth, notes, dur, time) {
  // Only trigger if all notes are valid
  if (
    Array.isArray(notes) &&
    notes.length > 0 &&
    notes.every(isValidNote)
  ) {
    synth.triggerAttackRelease(notes, dur, time);
  }
}

export default function BalancedGenerativeMusic() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tempo, setTempo] = useState(100);
  const [error, setError] = useState("");
  const synthsRef = useRef([]);
  const synthsLayer2Ref = useRef([]);
  const bassRef = useRef();
  const filterRef = useRef();
  const reverbRef = useRef();
  const delayRef = useRef();
  const gainRef = useRef();
  const gainLayer2Ref = useRef();
  const analyserRef = useRef();
  const panNodesRef = useRef({ layer1: [], layer2: [] });
  const patternRef = useRef({ primary: [], secondary: [] });
  const nextPatternRef = useRef({ primary: [], secondary: [] });
  const seqRef = useRef();
  const transitionRef = useRef({
    isTransitioning: false,
    progress: 0,
  });
  const stepRef = useRef(0);
  const lastScaleChangeRef = useRef(0);

  useEffect(() => {
    reverbRef.current = new Tone.Reverb({
      decay: 4.5,
      wet: 0.3,
      preDelay: 0.08,
    }).toDestination();

    delayRef.current = new Tone.FeedbackDelay({
      delayTime: "8n",
      feedback: 0.28,
      wet: 0.15,
    }).connect(reverbRef.current);

    filterRef.current = new Tone.Filter(1200, "lowpass").connect(delayRef.current);

    const compressor = new Tone.Compressor({
      threshold: -30,
      ratio: 6,
      attack: 0.003,
      release: 0.25,
    }).connect(filterRef.current);

    const crusher = new Tone.BitCrusher(4).connect(compressor);
    const phaser = new Tone.Phaser({
      frequency: 0.3,
      octaves: 2,
      baseFrequency: 350,
      wet: 0.25,
    }).connect(crusher);

    gainRef.current = new Tone.Gain(0.8).connect(phaser);
    gainLayer2Ref.current = new Tone.Gain(0.25).connect(phaser);

    analyserRef.current = new Tone.Analyser("waveform", 128).connect(gainRef.current);

    synthsRef.current = [];
    panNodesRef.current.layer1 = [];
    for (let i = 0; i < 2; i++) {
      const pan = new Tone.Panner(0).connect(gainRef.current);
      panNodesRef.current.layer1.push(pan);
      const synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: i === 0 ? "sawtooth" : "square" },
        envelope: { attack: 0.05, decay: 0.25, sustain: 0.6, release: 1.4 },
      })
        .connect(pan)
        .connect(analyserRef.current);
      synthsRef.current.push(synth);
    }

    synthsLayer2Ref.current = [];
    panNodesRef.current.layer2 = [];
    for (let i = 0; i < 2; i++) {
      const pan = new Tone.Panner(0).connect(gainLayer2Ref.current);
      panNodesRef.current.layer2.push(pan);
      const synth = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: i === 0 ? "triangle" : "pulse" },
        envelope: { attack: 0.1, decay: 0.4, sustain: 0.5, release: 2.0 },
      }).connect(pan);
      synthsLayer2Ref.current.push(synth);
    }

    bassRef.current = new Tone.MonoSynth({
      oscillator: { type: "square" },
      filter: { type: "lowpass", frequency: 200 },
      envelope: { attack: 0.02, decay: 0.2, sustain: 0.8, release: 0.4 },
    })
      .connect(gainRef.current)
      .connect(analyserRef.current);

    patternRef.current = {
      primary: generateNewPattern(true),
      secondary: generateNewPattern(false),
    };

    return () => {
      Tone.getTransport().stop();
      Tone.getTransport().cancel();
      synthsRef.current.forEach((s) => s.dispose());
      synthsLayer2Ref.current.forEach((s) => s.dispose());
      bassRef.current.dispose();
      filterRef.current.dispose();
      reverbRef.current.dispose();
      delayRef.current.dispose();
      gainRef.current.dispose();
      gainLayer2Ref.current.dispose();
      analyserRef.current.dispose();
      panNodesRef.current.layer1.forEach((p) => p.dispose());
      panNodesRef.current.layer2.forEach((p) => p.dispose());
    };
  }, []);

  useEffect(() => {
    const canvas = document.getElementById("waveform");
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;

    const drawWaveform = () => {
      if (!isPlaying || !ctx) return;
      const values = analyserRef.current.getValue();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.beginPath();
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 2;
      const width = canvas.width / values.length;
      for (let i = 0; i < values.length; i++) {
        const x = i * width;
        const y = ((1 + values[i]) * canvas.height) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      requestAnimationFrame(drawWaveform);
    };

    if (isPlaying) drawWaveform();

    return () => {
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [isPlaying]);

  const startMusic = async () => {
    setIsLoading(true);
    setError("");
    try {
      await Tone.start();
      if (Tone.context.state !== "running")
        throw new Error("Audio context failed to start.");

      setIsPlaying(true);
      setIsLoading(false);

      stepRef.current = 0;
      lastScaleChangeRef.current = 0;
      transitionRef.current = { isTransitioning: false, progress: 0 };
      patternRef.current = {
        primary: generateNewPattern(true),
        secondary: generateNewPattern(false),
      };

      seqRef.current = new Tone.Loop((time) => {
        let step = stepRef.current;

        if (
          step - lastScaleChangeRef.current >= 16 &&
          !transitionRef.current.isTransitioning
        ) {
          nextPatternRef.current = {
            primary: generateNewPattern(true),
            secondary: generateNewPattern(false),
          };
          transitionRef.current = { isTransitioning: true, progress: 0 };
          lastScaleChangeRef.current = step;
        }

        const notePrimary = transitionRef.current.isTransitioning
          ? interpolatePatterns(
              patternRef.current.primary,
              nextPatternRef.current.primary,
              transitionRef.current.progress,
              step % patternRef.current.primary.length
            )
          : patternRef.current.primary[
              step % patternRef.current.primary.length
            ];

        const noteSecondary = transitionRef.current.isTransitioning
          ? interpolatePatterns(
              patternRef.current.secondary,
              nextPatternRef.current.secondary,
              transitionRef.current.progress,
              step % patternRef.current.secondary.length
            )
          : patternRef.current.secondary[
              step % patternRef.current.secondary.length
            ];

        if (transitionRef.current.isTransitioning) {
          transitionRef.current.progress += 0.07;
          if (transitionRef.current.progress >= 1) {
            patternRef.current = { ...nextPatternRef.current };
            transitionRef.current.isTransitioning = false;
          }
        }

        // --- SAFETY: Only trigger if notePrimary is valid
        if (
          isValidNote(notePrimary) &&
          Math.random() > 0.1
        ) {
          const synthIndex = Math.floor(Math.random() * synthsRef.current.length);
          const synth = synthsRef.current[synthIndex];
          const velocity = 0.5 + Math.random() * 0.5;
          const pan = -0.5 + Math.random();
          panNodesRef.current.layer1[synthIndex].pan.rampTo(pan, 0.02);
          synth.volume.value = Tone.gainToDb(velocity);
          safeTriggerAttackRelease(synth, notePrimary, "8n", time);
          if (Math.random() < 0.15) {
            // Chord: both notes must be valid
            const secondNote = Tone.Frequency(notePrimary).transpose(7).toNote();
            if (isValidNote(secondNote)) {
              safeTriggerAttackReleaseChord(
                synth,
                [notePrimary, secondNote],
                "8n",
                time + 0.1
              );
            }
          }
        }

        // --- SAFETY: Only trigger if noteSecondary is valid
        if (
          isValidNote(noteSecondary) &&
          Math.random() > 0.4
        ) {
          const synthIndex = Math.floor(Math.random() * synthsLayer2Ref.current.length);
          const synth2 = synthsLayer2Ref.current[synthIndex];
          const pan = -0.6 + Math.random() * 1.2;
          panNodesRef.current.layer2[synthIndex].pan.rampTo(pan, 0.08);
          synth2.volume.value = Tone.gainToDb(0.25);
          safeTriggerAttackRelease(synth2, noteSecondary, "8n", time);
        }

        // --- SAFETY: Only trigger bass if notePrimary is valid and transposed freq is valid
        if (Math.random() > 0.4 && isValidNote(notePrimary)) {
          const bassFreq = Tone.Frequency(notePrimary).transpose(-12).toFrequency();
          if (isValidFrequency(bassFreq)) {
            safeTriggerAttackRelease(
              bassRef.current,
              bassFreq,
              Math.random() < 0.3 ? "8n" : "4n",
              time
            );
          }
        }

        if (Math.random() < 0.05) {
          const targetFreq = 800 + Math.random() * 900;
          filterRef.current.frequency.linearRampToValueAtTime(
            targetFreq,
            Tone.now() + 2 + Math.random() * 3
          );
        }

        if (Math.random() < 0.08) {
          const wet = 0.15 + Math.random() * 0.2;
          reverbRef.current.wet.rampTo(wet, 0.8);
        }

        if (Math.random() < 0.04) {
          delayRef.current.wet.setValueAtTime(0.35, time);
          delayRef.current.wet.linearRampTo(
            0,
            time + 0.35 + Math.random() * 0.6
          );
        }

        if (Math.random() < 0.12 && !transitionRef.current.isTransitioning) {
          const newBpm = 85 + Math.random() * 45;
          Tone.getTransport().bpm.rampTo(newBpm, 0.7);
          setTempo(Math.round(newBpm));
        }

        stepRef.current++;
      }, "8n").start(0);

      const initBpm = 85 + Math.random() * 45;
      Tone.getTransport().bpm.value = initBpm;
      setTempo(Math.round(initBpm));
      Tone.getTransport().start();
    } catch (err) {
      setError(
        `Audio Error: ${err.message}. Make sure audio is unmuted and permissions are granted.`
      );
      setIsLoading(false);
      console.error("Audio Error:", err);
    }
  };

  const stopMusic = () => {
    setIsPlaying(false);
    setIsLoading(false);
    setTempo(100);
    setError("");
    Tone.getTransport().stop();
    Tone.getTransport().cancel();
    stepRef.current = 0;
    lastScaleChangeRef.current = 0;
    transitionRef.current = { isTransitioning: false, progress: 0 };
  };

  const generateNewPattern = (isPrimary) => {
    const funkyScales = [
      ["A3", "C4", "D4", "E4", "G4", null],
      ["D3", "F#3", "A3", "C4", "E4", null],
      ["F3", "A3", "C4", "E4", "G4", null],
      ["G3", "B3", "D4", "F4", "A4", null],
    ];
    const baseScale = funkyScales[Math.floor(Math.random() * funkyScales.length)];
    // Only use valid notes for random selection
    const safeBaseScale = baseScale.filter(isValidNote);
    const length = 12 + Math.floor(Math.random() * 8);
    const newPattern = [];
    for (let i = 0; i < length; i++) {
      if (Math.random() < 0.2) {
        newPattern.push(null);
      } else {
        let note = safeBaseScale[Math.floor(Math.random() * safeBaseScale.length)];
        if (isPrimary && Math.random() < 0.15 && isValidNote(note)) {
          note = Tone.Frequency(note).transpose(12).toNote();
        }
        // Only push valid notes or null
        if (isValidNote(note)) {
          newPattern.push(note);
        } else {
          newPattern.push(null);
        }
      }
    }
    return newPattern;
  };

  const interpolatePatterns = (current, target, progress, index) => {
    // Only return valid notes or null
    const cur = current[index % current.length];
    const tgt = target[index % target.length];
    if (progress >= 1) return isValidNote(tgt) ? tgt : null;
    if (!isValidNote(cur) || !isValidNote(tgt)) {
      return Math.random() < progress
        ? (isValidNote(tgt) ? tgt : null)
        : (isValidNote(cur) ? cur : null);
    }
    return Math.random() < progress
      ? tgt
      : cur;
  };

  return (
    <div className="music-container">
      <form
        className="musicForm"
        style={{ width: "100%", maxWidth: 500, margin: "0 auto" }}
      >
        <h1 className="music-title">Music Generator</h1>
        <div className="form-group">
          <div id="status" className="text-sm font-mono mb-3">
            {error ||
              (isLoading
                ? "Loading Audio..."
                : isPlaying
                ? `Tempo: ${tempo} BPM`
                : "Ready to Chill")}
          </div>
        </div>
        <div className="form-group">
          <canvas
            id="waveform"
            className="w-full h-16 bg-gray-800 rounded mb-4"
          ></canvas>
        </div>
        <div className="form-group">
          {!isPlaying && !isLoading ? (
            <button
              type="button"
              onClick={startMusic}
              className="musicForm-button"
              style={{
                padding: "1rem",
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: 10,
                color: "var(--text-color)",
                backgroundColor: "#ffffff00",
                borderColor: "#ffffffe3",
                cursor: "pointer",
                transition: "all 0.3s ease",
                width: "100%",
                marginTop: "1rem",
              }}
            >
              Play
            </button>
          ) : (
            <button
              type="button"
              onClick={stopMusic}
              className="musicForm-button"
              style={{
                padding: "1rem",
                fontSize: "1.1rem",
                fontWeight: 600,
                borderRadius: 10,
                color: "var(--text-color)",
                backgroundColor: "#ffffff00",
                borderColor: "#ffffffe3",
                cursor: "pointer",
                transition: "all 0.3s ease",
                width: "100%",
                marginTop: "1rem",
              }}
              disabled={isLoading}
            >
              {isLoading ? "Loading..." : "Stop"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
